import { useCollection } from "@/features/collections/useCollection";
import { useCanonicalRecipientSummary } from '@/features/feedback-integration/useCanonicalRecipientSummary';
import { CanonicalNewFromCoach } from './FeedbackOverviewTab';
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import type {
  CoachingClip,
  CoachingNote,
  FeedbackBundle,
  FeedbackConversationTarget,
  PlayerGoal,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { TriangleAlert } from "lucide-react";
import { GoalCard } from "./components/GoalCard";
import { GoalReflectionModal } from "./components/GoalReflectionModal";
import { GoalDetailsModal } from "./components/GoalDetailsModal";
import { useMyPlayerDevelopment } from "./hooks/useDevelopmentProfile";
import {
  contextPillLabel,
  noteDisplayTitle,
  shortDateLabel,
} from "./feedbackHelpers";
import { matchLabelFor, matchesById } from "./feedbackTabs";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { buildPlayerResponseIndex } from "./responseSignals";
import { BoardChip, boardHasContent } from "./components/BoardChip";
import {
  FeedbackThumb,
  ObservationThumb,
} from "./components/FeedbackCardBits";

/**
 * Feedback Goals tab.
 *
 * The viewer development surface. Data comes from
 * `useMyPlayerDevelopment` for the active player; the page leads with
 * **what the player is working on**
 * (active goals + this week's focus) instead of leading with counts.
 *
 * Section order (player-first):
 *   1. Header — "What you're working on" + Active/Done counts.
 *   2. Active goals (large cards with Action plan + Success criteria
 *      already on `GoalCard`).
 *   3. This week's focus — focus areas from the development profile.
 *   4. Recent coach feedback — compact 3-up strip of recent positives +
 *      "to work on" + goal-set notes.
 *   5. Completed goals — quieter strip at the bottom.
 *   6. Recent clips + progress counts.
 *
 * Preserved invariants:
 *   - Coach modal (`PlayerDevelopmentProfile viewer={false}`) renders
 *     unchanged — this file does NOT modify the painter. The viewer
 *     reorder happens HERE, in the wrapper.
 *   - `useMyPlayerDevelopment` hits the viewer-scoped endpoint, which
 *     already omits `themes` + `linked_accounts`. We never read
 *     `coach_private_note` (already scrubbed server-side; defense in
 *     depth, no template).
 *
 * Out of scope for PR 3 (the plan defers them):
 *   - Per-goal weekly drill recommendation (mockup's blue panel).
 *   - "Linked clip" hover affordance — the existing GoalCard already
 *     surfaces source notes/clips; no UI change here.
 */

export type FeedbackGoalsTabProps = {
  onOpenFeedback?: (target: FeedbackConversationTarget) => void;
  onOverlayChange?(active: boolean): void;
  bundle: FeedbackBundle | undefined;
  reviewedNoteIds?: Set<number>;
  reviewedPlaylistIds?: Set<number>;
  reviewedClipIds?: Set<number>;
};

export function FeedbackGoalsTab({
  bundle,
  reviewedNoteIds,
  reviewedClipIds,
  onOpenFeedback,
  onOverlayChange,
}: FeedbackGoalsTabProps) {
  const players = bundle?.players ?? [];

  // Sticky active-player id, same pattern the legacy
  // FeedbackDevelopmentTab used.
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);
  useEffect(() => {
    if (players.length === 0) {
      setActivePlayerId(null);
      return;
    }
    setActivePlayerId((prev) =>
      prev && players.some((p) => p.id === prev) ? prev : players[0]!.id,
    );
  }, [players]);

  const devQuery = useMyPlayerDevelopment(activePlayerId);
  const canonicalSummary=useCanonicalRecipientSummary(activePlayerId,!!onOpenFeedback&&!!activePlayerId);
  const activePlayer =
    players.find((player) => player.id === activePlayerId) ?? players[0];
  const responseIndex = useMemo(
    () => buildPlayerResponseIndex(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // Modal wiring — same as the legacy host.
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [openGoal, setOpenGoal] = useState<PlayerGoal | null>(null);
  const [reflectGoal, setReflectGoal] = useState<PlayerGoal | null>(null);
  useEffect(() => {
    onOverlayChange?.(!!openNote || !!openClip || !!openGoal || !!reflectGoal);
    return () => onOverlayChange?.(false);
  }, [openNote, openClip, openGoal, reflectGoal, onOverlayChange]);

  // Completed goals come from the bundle (the dev profile only
  // surfaces active ones). `goals[]` is already viewer-filtered +
  // privacy-scrubbed server-side.
  const completedGoals = useMemo<PlayerGoal[]>(() => {
    if (!bundle?.goals) return [];
    const playerFilter = activePlayerId
      ? (g: PlayerGoal) => g.player_id === activePlayerId
      : () => true;
    return bundle.goals
      .filter(playerFilter)
      .filter((g) => g.status === "achieved" || g.status === "archived");
  }, [bundle?.goals, activePlayerId]);

  // Keep the independent Goal open, but never retain a source grant from the
  // object captured when the dialog opened. A fresh denial/removal wins.
  const currentOpenGoal = openGoal && openGoal.player_id === activePlayerId && !devQuery.isError
    ? [...(devQuery.data?.active_goals ?? []), ...completedGoals].find(goal => goal.id === openGoal.id)
    : undefined;

  if (players.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-development-no-players"
      >
        No roster player is linked to your account yet. Ask a coach to link you.
      </p>
    );
  }

  return (
    <div
      data-testid="feedback-goals-tab"
      /* Keep the legacy testid too — feedback-shell.spec.js asserts
       * `feedback-development-tab` is visible on `?tab=development`. */
      data-development-tab="true"
    >
      <div data-testid="feedback-development-tab">
        {players.length > 1 ? (
          <div
            className="flex flex-wrap gap-1.5 mb-4"
            role="tablist"
            aria-label="Linked player"
            data-testid="feedback-development-player-selector"
          >
            {players.map((p) => {
              const active = p.id === activePlayerId;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActivePlayerId(p.id)}
                  data-feedback-dev-player={p.id}
                  data-active={active}
                  className={[
                    "rounded px-3 py-1 text-xs font-display font-semibold uppercase tracking-wide transition-colors",
                    active
                      ? "bg-accent text-accent-ink"
                      : "border border-rule bg-surface text-ink-muted hover:bg-field hover:text-ink",
                  ].join(" ")}
                >
                  {p.jersey_number
                    ? `#${p.jersey_number} ${p.display_name}`
                    : p.display_name}
                </button>
              );
            })}
          </div>
        ) : null}

        {devQuery.isLoading ? (
          <p
            className="text-ink-muted text-sm font-mono px-3 py-6"
            data-testid="feedback-development-loading"
          >
            Loading your plan…
          </p>
        ) : devQuery.isError ? (
          <GoalsUnavailableState
            playerName={activePlayer?.display_name ?? ""}
            retrying={devQuery.isFetching}
            onRetry={() => void devQuery.refetch()}
          />
        ) : devQuery.data == null ? (
          <p
            className="text-ink-muted text-sm px-3 py-6"
            data-testid="feedback-development-unavailable"
          >
            Plan not available.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {/* 1. Header — name, headline, active/done counts, +
             * a status legend tooltip explaining what active means. */}
            <GoalsHeader
              activeGoals={devQuery.data.active_goals}
              doneCount={completedGoals.filter((goal) => goal.status === "achieved").length}
              playerName={devQuery.data.player.display_name}
            />

            {/* 2. Active goals — large cards. */}
            <ActiveGoalsSection
              goals={devQuery.data.active_goals}
              onReflect={setReflectGoal}
              onViewGoal={setOpenGoal}
            />

            {/* 3. "This week's focus" — derived server-side from
             * recent correction / individual-goal notes'
             * `what_to_do_next`. Hidden when empty. */}
            {!onOpenFeedback&&<WeeksFocusSection focusAreas={devQuery.data.current_focus_areas} />}

            {/* 4. Recent coach feedback — compact 3-up strip. */}
            {onOpenFeedback?<CanonicalNewFromCoach summary={canonicalSummary} onOpen={onOpenFeedback} title="Recent Feedback"/>:<RecentFeedbackStrip
              positives={devQuery.data.recent_positives}
              corrections={devQuery.data.recent_corrections}
              onOpenNote={setOpenNote}
            />}

            {/* 5. Approved grouped placement for production-only history:
             * completed goals occupy the larger panel; recent clips and
             * honest progress counts share the supporting panel. */}
            <PastProgressSection
              completedGoals={completedGoals}
              clips={devQuery.data.recent_clips}
              bundle={bundle}
              profile={devQuery.data}
              onOpenClip={setOpenClip}
              canonical={onOpenFeedback?canonicalSummary:undefined}
            />
          </div>
        )}
      </div>

      {/* Modals — note / clip + reflection. */}
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{ id: openNote.match_id ?? "", team_id: openNote.team_id }}
          onClose={() => setOpenNote(null)}
          players={players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id, team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={players}
          reviewedClipIds={reviewedClipIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {reflectGoal ? (
        <GoalReflectionModal
          goal={reflectGoal}
          onClose={() => setReflectGoal(null)}
        />
      ) : null}
      {openGoal ? (
        <GoalDetailsModal
          onOpenFeedback={onOpenFeedback}
          goal={{ ...openGoal, source_feedback: currentOpenGoal?.source_feedback ?? null }}
          playerName={activePlayer?.display_name ?? ""}
          onClose={() => setOpenGoal(null)}
          onReflect={(goal) => {
            setOpenGoal(null);
            setReflectGoal(goal);
          }}
          onOpenSource={(goal) => {
            setOpenGoal(null);
            if (goal.source_note) setOpenNote(goal.source_note);
            else if (goal.source_clip) setOpenClip(goal.source_clip);
          }}
        />
      ) : null}
    </div>
  );
}

function GoalsUnavailableState({
  playerName,
  retrying,
  onRetry,
}: {
  playerName: string;
  retrying: boolean;
  onRetry: () => void;
}) {
  const firstName = playerName.split(/\s+/)[0]?.trim();
  return (
    <div
      className="family-goals-unavailable"
      data-testid="feedback-development-error"
    >
      <header className="family-feedback-page-heading">
        <p className="eyebrow">
          {firstName ? `${firstName}'s plan` : "Your plan"}
        </p>
        <h2>What you're working on.</h2>
        <p>Set by your coach. Updated as your coach adds new goals.</p>
      </header>
      <div className="family-goals-unavailable-grid">
        <div className="family-goals-placeholder" aria-hidden="true">
          <p className="eyebrow">Your plan</p>
          <span />
          <span />
          <span />
        </div>
        <section
          className="family-goals-recovery"
          aria-labelledby="feedback-goals-recovery-heading"
        >
          <div className="family-feedback-state-icon" aria-hidden="true">
            <TriangleAlert />
          </div>
          <h3 id="feedback-goals-recovery-heading">
            Your goals didn’t load.
          </h3>
          <p>
            Your feedback is still available. Try loading just this plan
            again.
          </p>
          <Button type="button" disabled={retrying} onClick={onRetry}>
            {retrying ? "Trying again…" : "Try goals again"}
          </Button>
        </section>
      </div>
    </div>
  );
}

function PastProgressSection({
  completedGoals,
  clips,
  bundle,
  profile,
  onOpenClip,
  canonical,
}: {
  completedGoals: PlayerGoal[];
  clips: CoachingClip[];
  bundle: FeedbackBundle | undefined;
  profile: import("@/lib/types").DevelopmentProfile;
  onOpenClip: (c: CoachingClip) => void;
  canonical?:ReturnType<typeof useCanonicalRecipientSummary>;
}) {
  const hasProgress =
    profile.review_status.notes.assigned_count > 0 ||
    profile.review_status.reflection_count > 0;
  const hasCompletedGoals = completedGoals.length > 0;
  const hasSupportingProgress = canonical ? canonical.feedbackTotal!==0||canonical.recapTotal!==0 : clips.length > 0 || hasProgress;

  if (!hasCompletedGoals && !hasSupportingProgress) return null;

  return (
    <section
      className="family-goals-retained"
      aria-labelledby="past-progress-heading"
      data-testid="feedback-goals-past-progress"
    >
      <header className="family-goals-retained-header">
        <div>
          <p className="eyebrow">{canonical?'Your record':'Retained production features'}</p>
          <h2 id="past-progress-heading">Past progress</h2>
        </div>
        <p>
          {canonical?'Completed goals and Feedback shared with you. Reading Feedback does not change your goals.':'Completed goals, recent clips and honest progress counts remain available after the approved active-plan content.'}
        </p>
      </header>
      <div
        className="family-goals-retained-grid"
        data-single-panel={
          hasCompletedGoals && hasSupportingProgress ? "false" : "true"
        }
      >
        {hasCompletedGoals ? (
          <div
            className="family-goals-retained-panel"
            data-testid="feedback-goals-completed-panel"
          >
            <CompletedGoalsSection goals={completedGoals} />
          </div>
        ) : null}
        {hasSupportingProgress ? (
          <div
            className="family-goals-retained-panel family-goals-supporting-panel"
            data-testid="feedback-goals-supporting-panel"
          >
            {canonical?<CanonicalSharedCounts summary={canonical}/>:<><RecentClipsSection
              clips={clips}
              bundle={bundle}
              onOpenClip={onOpenClip}
            />
            <ProgressSection profile={profile} /></>}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function CanonicalSharedCounts({summary}:{summary:ReturnType<typeof useCanonicalRecipientSummary>}){
  return <section aria-label="Shared Feedback counts" className="space-y-3"><h3 className="font-display">Shared with you</h3>
    <dl className="grid grid-cols-2 gap-3 text-sm">{([{label:'Feedback',value:summary.feedbackTotal,query:summary.recent},
      {label:'Needs attention',value:summary.attentionFeedbackTotal,query:summary.attention},{label:'Match recaps',value:summary.recapTotal,query:summary.recaps}]).map(item=><div key={item.label}>
      <dt className="text-ink-muted">{item.label}</dt><dd>{item.query.isError?<Button size="sm" variant="ghost" onClick={()=>void item.query.refetch()}>Retry {item.label}</Button>
        :item.value===undefined?<span role="status">Loading…</span>:<strong>{item.value}</strong>}</dd></div>)}</dl>
  </section>;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function GoalsHeader({
  activeGoals,
  doneCount,
  playerName,
}: {
  activeGoals: PlayerGoal[];
  doneCount: number;
  playerName: string;
}) {
  // Use the player's first name in the kicker if available — friendly
  // and falls back to "Your plan" gracefully.
  const firstName = playerName?.split(/\s+/)[0]?.trim();
  // Break the active set down by status so the tooltip can explain
  // what "active" includes. The three statuses below all count as
  // active per `ACTIVE_GOAL_STATUSES`.
  const openCount = activeGoals.filter((g) => g.status === "open").length;
  const inProgressCount = activeGoals.filter(
    (g) => g.status === "in_progress",
  ).length;
  const needsFollowUpCount = activeGoals.filter(
    (g) => g.status === "needs_follow_up",
  ).length;
  const tooltipParts: string[] = [];
  if (openCount > 0) tooltipParts.push(`${openCount} open`);
  if (inProgressCount > 0) tooltipParts.push(`${inProgressCount} in progress`);
  if (needsFollowUpCount > 0)
    tooltipParts.push(`${needsFollowUpCount} needs follow-up`);
  const tooltip = tooltipParts.length
    ? `Active includes: ${tooltipParts.join(" · ")}.`
    : 'No active goals. "Active" means a goal in open, in progress, or needs follow-up.';
  return (
    <header
      className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
      data-testid="feedback-goals-header"
    >
      <div>
        <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {firstName ? `${firstName}'s plan` : "Your plan"}
        </p>
        <h2 className="font-display text-xl sm:text-2xl tracking-tight text-ink mt-1">
          What you're working on.
        </h2>
        <p className="text-sm text-ink-muted mt-1">
          Set by your coach. Updated as your coach adds new goals.
        </p>
      </div>
      <div className="flex gap-6" data-testid="feedback-goals-counts">
        <CountBlock
          label="Active goals"
          value={activeGoals.length}
          testid="goals-active-count"
          tooltip={tooltip}
        />
        <CountBlock
          label="Coach marked achieved"
          value={doneCount}
          tone="good"
          testid="goals-done-count"
          tooltip="Goals your coach marked achieved."
        />
      </div>
    </header>
  );
}

function CountBlock({
  label,
  value,
  tone,
  testid,
  tooltip,
}: {
  label: string;
  value: number;
  tone?: "good";
  testid: string;
  tooltip?: string;
}) {
  return (
    <div
      data-testid={testid}
      title={tooltip}
      className={tooltip ? "cursor-help" : undefined}
    >
      <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </p>
      <p
        className={`font-display text-2xl font-semibold ${
          tone === "good" ? "text-good" : "text-ink"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active goals
// ---------------------------------------------------------------------------

function ActiveGoalsSection({
  goals,
  onReflect,
  onViewGoal,
}: {
  goals: PlayerGoal[];
  onReflect: (g: PlayerGoal) => void;
  onViewGoal: (g: PlayerGoal) => void;
}) {
  const collection = useCollection(goals, {
    scope: `family-active-goals:${goals[0]?.player_id ?? ""}`,
    text: (g) => `${g.title} ${g.description ?? ""}`,
  });
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="active-goals-heading"
      data-testid="feedback-goals-active"
    >
      <h3
        id="active-goals-heading"
        className="sr-only"
      >
        Active goals
      </h3>
      <CollectionToolbar collection={collection} label="active goals" />
      {goals.length === 0 ? (
        <p
          className="text-sm text-ink-muted"
          data-testid="feedback-goals-active-empty"
        >
          No goals shared yet. When your coach creates a goal for you, it will
          appear here with an action plan and success criteria.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {collection.items.map((g, index) => (
            <GoalCard
              key={g.id}
              goal={g}
              viewer
              defaultExpanded
              tone={index === 0 ? "accent" : "default"}
              onAddReflection={() => onReflect(g)}
              onViewDetails={() => onViewGoal(g)}
            />
          ))}
        </div>
      )}
      <CollectionPagination collection={collection} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// This week's focus
// ---------------------------------------------------------------------------

function WeeksFocusSection({
  focusAreas,
}: {
  focusAreas: import("@/lib/types").DevelopmentFocusArea[];
}) {
  if (!focusAreas || focusAreas.length === 0) return null;
  const focus = focusAreas[0]!;
  return (
    <section
      className="family-goals-focus-hero"
      aria-labelledby="weeks-focus-heading"
      data-testid="feedback-goals-focus"
    >
      <p className="eyebrow">This week's focus</p>
      <h3 id="weeks-focus-heading" data-testid="feedback-goals-focus-item">
        {focus.what_to_do_next}
      </h3>
      <p>From your recent shared feedback.</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recent coach feedback
// ---------------------------------------------------------------------------

function RecentFeedbackStrip({
  positives,
  corrections,
  onOpenNote,
}: {
  positives: CoachingNote[];
  corrections: CoachingNote[];
  onOpenNote: (n: CoachingNote) => void;
}) {
  // Pick two in alternating order — the approved surface uses a
  // compact divided list rather than a dashboard of mini cards.
  const picks: { note: CoachingNote; tone: "good" | "flag" }[] = [];
  const p = [...positives];
  const c = [...corrections];
  while (picks.length < 2 && (p.length > 0 || c.length > 0)) {
    if (p.length > 0) picks.push({ note: p.shift()!, tone: "good" });
    if (picks.length < 2 && c.length > 0)
      picks.push({ note: c.shift()!, tone: "flag" });
  }
  if (picks.length === 0) return null;
  return (
    <section
      className="family-goals-feedback-panel"
      aria-labelledby="recent-feedback-heading"
      data-testid="feedback-goals-recent-feedback"
    >
      <header>
        <h3 id="recent-feedback-heading">Recent coach feedback.</h3>
        <Link to="/feedback" search={{ tab: "review" }}>
          All feedback
        </Link>
      </header>
      <div className="family-goals-feedback-list">
        {picks.map(({ note, tone }) => (
          <RecentFeedbackCard
            key={note.id}
            note={note}
            tone={tone}
            onOpen={() => onOpenNote(note)}
          />
        ))}
      </div>
    </section>
  );
}

function RecentFeedbackCard({
  note,
  tone,
  onOpen,
}: {
  note: CoachingNote;
  tone: "good" | "flag";
  onOpen: () => void;
}) {
  const title = (
    note.title?.trim() ||
    note.player_summary?.trim() ||
    note.body?.trim() ||
    "Coach feedback"
  ).slice(0, 80);
  const dateLabel = note.event_date
    ? shortDateLabel(note.event_date)
    : note.created_at
      ? shortDateLabel(note.created_at)
      : "";
  const isObservation = (note.note_context ?? "video") === "observation";
  const hasBoard = isObservation && boardHasContent(note.tactical_board_json);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="feedback-goals-recent-feedback-card"
      data-tone={tone}
      className="family-goals-feedback-row"
    >
      {hasBoard ? (
        <BoardChip raw={note.tactical_board_json} />
      ) : isObservation ? (
        <ObservationThumb />
      ) : (
        <FeedbackThumb
          kind="note"
          id={note.id}
          hasThumbnail={note.has_thumbnail !== false}
        />
      )}
      <span>
        <strong>{noteDisplayTitle(note) || title}</strong>
        <small>
          {contextPillLabel(note)}
          {dateLabel ? ` · ${dateLabel}` : ""}
        </small>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Completed goals
// ---------------------------------------------------------------------------

function CompletedGoalsSection({ goals }: { goals: PlayerGoal[] }) {
  const collection = useCollection(goals, {
    scope: `family-completed-goals:${goals[0]?.player_id ?? ""}`,
    text: (g) => g.title,
  });
  if (goals.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="completed-goals-heading"
      data-testid="feedback-goals-completed"
    >
      <h3
        id="completed-goals-heading"
        className="font-display text-lg tracking-tight text-ink"
      >
        Completed goals
      </h3>
      <CollectionToolbar collection={collection} label="completed goals" />
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {collection.items.map((g) => (
          <li
            key={g.id}
            className="rounded border border-rule bg-surface p-3 opacity-70 shadow-sm"
            data-testid="feedback-goals-completed-card"
          >
            <p className="font-display text-sm text-ink">{g.title}</p>
            <Badge variant="good">
              {g.status === "archived" ? "Archived" : "Achieved"}
            </Badge>
            {g.target_date ? (
              <p className="completed-goal-date text-[0.65rem] font-mono uppercase tracking-wider text-ink-muted">
                {`by ${shortDateLabel(g.target_date)}`}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      <CollectionPagination collection={collection} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recent clips — only render when non-empty.
// ---------------------------------------------------------------------------

function RecentClipsSection({
  clips,
  bundle,
  onOpenClip,
}: {
  clips: CoachingClip[];
  bundle: FeedbackBundle | undefined;
  onOpenClip: (c: CoachingClip) => void;
}) {
  const matches = useMemo(() => matchesById(bundle), [bundle]);
  if (!clips || clips.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="recent-clips-heading"
      data-testid="feedback-goals-recent-clips"
    >
      <h3
        id="recent-clips-heading"
        className="font-display text-lg tracking-tight text-ink"
      >
        Recent clips
      </h3>
      <ul className="flex flex-col gap-2">
        {clips.map((c) => {
          const matchLabel = matchLabelFor(matches, c.match_id);
          const range = `${formatPlayhead(c.start_seconds)}–${formatPlayhead(c.end_seconds)}`;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onOpenClip(c)}
                data-testid="feedback-goals-recent-clip-row"
                className="flex w-full items-center justify-between gap-3 rounded border border-rule bg-surface px-3 py-2 text-left shadow-sm transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <span className="min-w-0">
                  <span className="block font-display text-sm text-ink truncate">
                    {c.title?.trim() || "Match clip"}
                  </span>
                  <span className="block text-xs text-ink-muted font-mono mt-0.5 truncate">
                    {matchLabel ? `${matchLabel} · ` : ""}
                    {range}
                  </span>
                </span>
                <span className="text-[0.7rem] font-display uppercase tracking-wider text-accent-text shrink-0">
                  Watch →
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Progress — the corrected counts row that replaces the painter's
// misleading "Notes shared" / missing "Clips reviewed" Summary panel.
// ---------------------------------------------------------------------------

function ProgressSection({
  profile,
}: {
  profile: import("@/lib/types").DevelopmentProfile;
}) {
  const r = profile.review_status;
  // The four numbers that matter to a player who's working on their
  // plan. Labels are activity-shaped, not object-shaped:
  //   - "Notes done"  → what they've worked through (vs the
  //     painter's "Notes shared" which was confusingly a strict
  //     `player_ids`-tagged count)
  //   - "Reflections" → free-text or signal responses they've sent
  // We omit any tile whose denominator is 0 — the player doesn't
  // need a "0 of 0" sitting on the page.
  type Tile = { label: string; value: string; testid: string };
  const tiles: Tile[] = [];
  if (r.notes.assigned_count > 0) {
    tiles.push({
      label: "Notes done",
      value: `${r.notes.reviewed_count} / ${r.notes.assigned_count}`,
      testid: "feedback-goals-progress-notes",
    });
  }
  // Clips: review tracking on clips landed in PR 7 of the redesign,
  // but the development profile's `review_status.clips` shape only
  // exposes `assigned_count` + `review_supported` today (no
  // `reviewed_count`). A follow-up will extend the server payload
  // so we can show "Clips done X / Y"; for now the Clips count
  // lives on the player's bundle and shows up via the Review tab's
  // Reviewed filter — surfacing a number here without a denominator
  // we trust would be worse than omitting it.
  if (r.reflection_count > 0) {
    tiles.push({
      label: "Reflections",
      value: String(r.reflection_count),
      testid: "feedback-goals-progress-reflections",
    });
  }
  if (tiles.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="progress-heading"
      data-testid="feedback-goals-progress"
    >
      <h3
        id="progress-heading"
        className="font-display text-lg tracking-tight text-ink"
      >
        Progress
      </h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((t) => (
          <div
            key={t.label}
            data-testid={t.testid}
            className="rounded border border-rule bg-surface px-3 py-2 shadow-sm"
          >
            <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              {t.label}
            </p>
            <p className="font-display text-lg font-semibold text-ink mt-1">
              {t.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
